import { useEffect, useRef } from "react";
import { EditorView, basicSetup } from "codemirror";
import { markdown } from "@codemirror/lang-markdown";
import { EditorState, Compartment } from "@codemirror/state";
import { oneDark } from "@codemirror/theme-one-dark";
import { useTheme } from "../context/ThemeContext";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  isGenerating?: boolean;
  className?: string;
}

// Min height ≈ 6 lines (Tailwind spacing.48 = 192px)
const MIN_HEIGHT = 192;

export default function MarkdownEditor({
  value,
  onChange,
  label,
  isGenerating = false,
  className = "",
}: MarkdownEditorProps) {
  const { theme } = useTheme();
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const readOnlyCompartment = useRef(new Compartment());

  // Create / recreate editor when theme changes
  useEffect(() => {
    if (!editorRef.current) return;

    const view = new EditorView({
      doc: value,
      extensions: [
        basicSetup,
        markdown(),
        EditorView.lineWrapping,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChange(update.state.doc.toString());
          }
        }),
        ...(theme === "dark" ? [oneDark] : []),
        EditorView.theme({
          "&": {
            border: "1px solid rgb(226 232 240)",
            borderRadius: "6px",
            minHeight: `${MIN_HEIGHT}px`,
          },
          ".cm-scroller": { overflow: "visible" },
          ".cm-content": { minHeight: `${MIN_HEIGHT}px` },
        }),
        readOnlyCompartment.current.of(EditorState.readOnly.of(isGenerating)),
      ],
      parent: editorRef.current,
    });

    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  // Dynamically update readOnly via Compartment (no rebuild needed)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: readOnlyCompartment.current.reconfigure(
        EditorState.readOnly.of(isGenerating)
      ),
    });
  }, [isGenerating]);

  // Sync external value changes into editor
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current !== value) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value },
      });
    }
  }, [value]);

  return (
    <div className={className}>
      <div
        className={[
          "transition-opacity duration-200",
          isGenerating ? "opacity-50 pointer-events-none" : "",
        ].filter(Boolean).join(" ")}
        data-editor-content
        data-generating={isGenerating ? "true" : undefined}
      >
        <div
          ref={editorRef}
          className="min-h-48"
          aria-label={label ? `${label} editor` : "editor"}
        />
      </div>
    </div>
  );
}
